import { __decorate } from "tslib";
import { property, query } from 'lit/decorators.js';
import { DedupeMixin, wasApplied } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/mixins/dedupe.js';
import { convertDateToUSFormat } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/utils/date.js';
/**
 * @param Base The base class.
 * @returns A mix-in implementing validation logic.
 */
export const ValidityMixin = (Base) => {
    if (wasApplied(ValidityMixin, Base)) {
        return Base;
    }
    class Mixin extends Base {
        _getFirstErroredElement() {
            var _a, _b;
            return (_b = (_a = this.closest('cipublic-form')) === null || _a === void 0 ? void 0 : _a.querySelector('.error')) === null || _b === void 0 ? void 0 : _b.shadowRoot;
        }
        isDateInputValid(target) {
            const { format, maxDate, minDate } = this;
            // https://stackoverflow.com/a/31732581
            const convertedTarget = format === 'universal' ? convertDateToUSFormat(target.value) : target.value;
            const convertedMinDate = minDate ? convertDateToUSFormat(minDate) : '';
            const convertedMaxDate = maxDate ? convertDateToUSFormat(maxDate) : '';
            /*
              call `getTime()` in order to use comparison operators on dates
              https://stackoverflow.com/a/493018
            */
            const isGreaterOrEqualToMinDate = !minDate || new Date(convertedTarget).getTime() >= new Date(convertedMinDate).getTime();
            const isLessThanOrEqualToMaxDate = !maxDate || new Date(convertedTarget).getTime() <= new Date(convertedMaxDate).getTime();
            return isGreaterOrEqualToMinDate && isLessThanOrEqualToMaxDate;
        }
        _testValidity(target) {
            const { validity } = target;
            if (validity.patternMismatch)
                return `${this.errorPatternMismatch}`;
            if (validity.typeMismatch)
                return `${this.errorTypeMismatch}`;
            if (validity.valueMissing)
                return `${this.errorValueMissing}`;
            if (this.value !== '' && !this.isDateInputValid(target)) {
                return `${this.errorDateRange}`;
            }
            if (this.errorCustomMessage) {
                return `${this.errorCustomMessage}`;
            }
            return '';
        }
        _setErrorAttributes(target, status) {
            const { _errorContainer, _errorText, _hintText } = this;
            target.setAttribute('aria-invalid', 'true');
            if (_hintText) {
                _hintText.style.display = 'none';
            }
            if (_errorContainer) {
                _errorContainer.style.display = 'flex';
                _errorText.innerText = status;
            }
            this.className = 'error';
        }
        _removeErrorAttributes(target) {
            const { _errorContainer, _errorText, _hintText } = this;
            target.setAttribute('aria-invalid', 'false');
            if (_hintText) {
                _hintText.style.display = 'flex';
            }
            if (_errorContainer) {
                _errorContainer.style.display = 'none';
                _errorText.innerText = '';
            }
            this.className = '';
        }
        _showError(target) {
            const status = this._testValidity(target);
            if (status !== '') {
                this._setErrorAttributes(target, status);
                return true;
            }
            return false;
        }
        _showSuccess(target) {
            const status = this._testValidity(target);
            if (status === '') {
                this._removeErrorAttributes(target);
                return true;
            }
            return false;
        }
    }
    __decorate([
        query('.error-container')
    ], Mixin.prototype, "_errorContainer", void 0);
    __decorate([
        property({ reflect: true, attribute: 'error-custom-message' })
    ], Mixin.prototype, "errorCustomMessage", void 0);
    __decorate([
        property({ reflect: true, attribute: 'error-date-range' })
    ], Mixin.prototype, "errorDateRange", void 0);
    __decorate([
        property({ reflect: true, attribute: 'error-pattern-mismatch' })
    ], Mixin.prototype, "errorPatternMismatch", void 0);
    __decorate([
        query('.error-container p')
    ], Mixin.prototype, "_errorText", void 0);
    __decorate([
        property({ reflect: true, attribute: 'error-type-mismatch' })
    ], Mixin.prototype, "errorTypeMismatch", void 0);
    __decorate([
        property({ reflect: true, attribute: 'error-value-missing' })
    ], Mixin.prototype, "errorValueMissing", void 0);
    __decorate([
        query('.hint')
    ], Mixin.prototype, "_hintText", void 0);
    DedupeMixin(ValidityMixin, Mixin);
    // @ts-ignore TODO: Cannot assign an abstract constructor type to a non-abstract constructor type.
    return Mixin;
};
//# sourceMappingURL=validity.js.map